import { useId } from "react";
import { Recommendation } from "@/types";
import { useDateFormatter, useTranslations } from "@/contexts/Translation";
import { H3 } from "../Headers/Headings";
import Quote from "../Quote/Quote";
import { getQuoteName } from "@/lib/getQuoteName";
import { fixNewlines } from "@/lib/text";
import styles from "./CardRecommendations.module.css";

type CardRecommendationsProps = {
  recommendations?: Recommendation[];
  /** The public card page: each quote as its own white card, never with an email address. */
  variant?: "default" | "public";
};

// Recommendations the card owner chose to publish alongside the card. They are supporting
// evidence, not the headline — the scores stay the card itself.
const CardRecommendations = ({ recommendations, variant = "default" }: CardRecommendationsProps) => {
  const t = useTranslations("card");
  const d = useDateFormatter();
  const headingId = useId();

  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  if (variant === "public") {
    return (
      <section className={styles.public} aria-labelledby={headingId} data-testid='card-recommendations'>
        <p className={styles.eyebrow}>{t("publicRecommendationsEyebrow")}</p>
        <h2 id={headingId} className={styles.heading}>
          {t("publicRecommendationsHeading")}
        </h2>
        <p className={styles.lead}>{t("publicRecommendationsLead")}</p>
        <ul className={styles.list}>
          {recommendations.map((r, i) => (
            <li className={styles.publicItem} key={i}>
              <span className={styles.mark} aria-hidden='true'>
                &ldquo;
              </span>
              <blockquote className={styles.quote}>{fixNewlines(r.recommendation || "")}</blockquote>
              <p className={styles.by}>
                {r.recommendationName && <strong>{r.recommendationName}</strong>}
                {r.recommendationTitle && <span>{r.recommendationTitle}</span>}
                {r.respondedAt && <span>{d(r.respondedAt)}</span>}
              </p>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section className={styles.recommendations} aria-labelledby={headingId}>
      <H3 id={headingId}>{t("recommendationsHeading")}</H3>
      <ul className={styles.list}>
        {recommendations.map((r) => (
          <li className={styles.item} key={r.id}>
            <Quote date={r.respondedAt} from={getQuoteName(r.recommendationName, r.recommendationTitle, r.email)}>
              {fixNewlines(r.recommendation || "")}
            </Quote>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default CardRecommendations;
